import { IMicrosoftTeamsChannel } from './msTeams';
import { ISlackChannel, ISlackUser, ISlackUserGroup } from './slack';
import { IUser } from './user';

export enum ChatContextType {
  MEETING = 0,
  CALL = 0,
  DEAL = 1,
  GLOBAL = 2,
  COMPANY = 3
}

export enum EScheduledPromptState {
  ARCHIVED = 0,
  PAUSED = 1,
  ACTIVE = 2
}

export enum EScheduledPromptTypes {
  PRIVATE = 0,
  ORGANIZATION = 1,
  DEFAULT = 2
}

export interface IScheduledPromptSchedule {
  uuid: string;
  /**
   * RRULE string for frequency schedules; use `''` for meeting-end triggers when the property is present.
   */
  rrule?: string;
  /**
   * Not sent on create/update (FE-only `scheduleKind` state). Optional on read for legacy;
   * otherwise infer meeting-end when `rrule` is absent or empty.
   */
  scheduleKind?: 'frequency' | 'meeting_end';
  state: EScheduledPromptState;
  unreadChatsCount: number;
  created: string;
  modified: string;
  emailNotificationSettings: {
    user: string[];
    team: string[];
  };
  lastRan?: string;
  msTeamsChannels: IMicrosoftTeamsChannel[];
  slackChannels: ISlackChannel[];
  slackUserGroups: ISlackUserGroup[];
  slackUsers: ISlackUser[];
  /** Present when this schedule is driven by meeting-end automation rules. */
  automationUuid?: string;
}

// Response format from BE (camelized) - contains UUIDs only
export interface INotificationTargets {
  slack?: {
    slackChannelUuids?: string[];
    slackUserUuids?: string[];
    slackUserGroupUuids?: string[];
  };
  msTeams?: {
    msTeamsChannelUuids?: string[];
  };
  email?: {
    users: IAlertUser[];
    teams: IAlertTeam[];
  };
}

interface IAlertUser {
  user?: {
    email?: string;
  };
  email?: string;
  uuid?: string;
}

interface IAlertTeam {
  uuid: string;
  name: string;
}

export interface IPromptCollection {
  uuid: string;
  name: string;
  description?: string;
  icon?: string;
  defaultId?: string;
  user?: IUser;
}

export interface IScheduledPrompt {
  uuid: string;
  text: string;
  contextType: ChatContextType;
  scheduledPrompts: IScheduledPromptSchedule[];
  scheduledUsers: IUser[];
  user: IUser;
  created: string;
  modified: string;
  name: string;
  promptType: EScheduledPromptTypes;
  collection?: IPromptCollection | null;
  defaultId?: string;
  lastUsed?: string;
  /** Meeting-end schedules backed by `/api/v1/automation/` (may be set when `scheduledPrompts` is empty). */
  automationUuid?: string;
}

export interface IChatContext {
  uuid: string | null;
  name: string;
  contextUuid: string | null;
  contextType: ChatContextType;
  label?: string;
  scheduledPromptUuid?: string | null;
  scheduledPromptName?: string | null;
}

/** Passed to Copilot "new chat" after deleting the active thread so entity scope is preserved. */
export type ICopilotCreateNewChatOptions = {
  preserveEntityScope?: Pick<IChatContext, 'contextType' | 'contextUuid'>;
};

/**
 * Mention row category (mirrors Copilot context-mention picker keys). Used when building
 * `data_json.context_uuids` and `data_json.mentions` (`member_emails`, `team_uuids`) for the
 * Copilot WebSocket send payload.
 */
export type CopilotAdditionalContextCategoryKey =
  | 'meetings'
  | 'deals'
  | 'companies'
  | 'teams'
  | 'members'
  | 'playlists';

/**
 * Client-only snapshot for rendering “Add context” pills above a user bubble; not sent on the wire.
 * Survives optimistic → server merge via {@link mergeCopilotChatDetailMessages}.
 */
export interface ICopilotStandaloneContextPillDisplay {
  id: string;
  display: string;
  /**
   * Optional disambiguator shown under {@link display} in the expanded chat-message
   * pill list — e.g. the meeting date when two pills share a subject (current upcoming
   * vs. last-completed-in-series).
   */
  secondary?: string;
  /**
   * Company rows: domain line (lowercase), aligned with {@link CompanyFilter} search results.
   * When set, domain is not duplicated inside {@link secondary}.
   */
  companyDomain?: string;
  /** Company rows: CRM record URL (opens in a new tab). */
  crmObjectUrl?: string;
  categoryKey?: CopilotAdditionalContextCategoryKey;
}

/** One row in `data_json.context_uuids.*` or `mentions.*` on the Copilot WebSocket payload. */
export interface ICopilotStreamContextAttachment {
  attachment_type?: 0 | 1;
  uuid: string;
  label?: string;
  /** Rich company/account rows may send a display name without `label`. */
  name?: string;
  domain?: string;
  object_url?: string;
  objectUrl?: string;
  link?: string;
  owner?: {
    name?: string;
    uuid?: string;
    email?: string | null;
  };
}

export interface ICopilotAdditionalContextMention {
  id: string;
  categoryKey: CopilotAdditionalContextCategoryKey;
  /** For `members`: email for `data_json.mentions.member_emails` when `id` is a uuid. */
  email?: string;
  /** 0 = mentioned (@ chip), 1 = attached Add-context pill (page scope uses 1). */
  attachmentType?: 0 | 1;
  /** Human-readable name for the wire attachment row. */
  label?: string;
  /**
   * For `companies`: optional fields copied onto `context_uuids.account_uuids` rows
   * (`domain`, `object_url`) when present.
   */
  companyDomain?: string;
  crmObjectUrl?: string;
}

export interface IContextMap {
  [key: string]: {
    uuid: string;
    type: 'meeting' | 'meeting_engagement' | 'email_engagement';
    metadata?: {
      timestamp: string;
    };
  };
}

export interface IThinkingMessage {
  message: string;
  timestamp: string;
}

/** Buckets mirrored on the wire in `data_json.context_uuids` (snake or camel). */
export interface ICopilotMessageDataJsonContextUuids {
  meetingUuids?: Array<string | ICopilotStreamContextAttachment>;
  meeting_uuids?: Array<string | ICopilotStreamContextAttachment>;
  dealUuids?: Array<string | ICopilotStreamContextAttachment>;
  deal_uuids?: Array<string | ICopilotStreamContextAttachment>;
  accountUuids?: Array<string | ICopilotStreamContextAttachment>;
  account_uuids?: Array<string | ICopilotStreamContextAttachment>;
  playlistUuids?: Array<string | ICopilotStreamContextAttachment>;
  playlist_uuids?: Array<string | ICopilotStreamContextAttachment>;
}

/** Ask Avoma send payload uses plain string arrays; persisted rows may still use attachment rows. */
export interface ICopilotMessageDataJsonMentions {
  memberEmails?: Array<string | ICopilotStreamContextAttachment>;
  member_emails?: Array<string | ICopilotStreamContextAttachment>;
  teamUuids?: Array<string | ICopilotStreamContextAttachment>;
  team_uuids?: Array<string | ICopilotStreamContextAttachment>;
}

export interface IChatMessage {
  uuid?: string;
  created?: string;
  author: number;
  message: string;
  /** Popover-only context (not inlined in `message`); rendered as chips above the user bubble. */
  copilotStandaloneContextPills?: ICopilotStandaloneContextPillDisplay[];
  dataJson?: {
    contextType: ChatContextType;
    contextUuid: string;
    contextMap: IContextMap;
    contextUuids?: ICopilotMessageDataJsonContextUuids;
    context_uuids?: ICopilotMessageDataJsonContextUuids;
    mentions?: ICopilotMessageDataJsonMentions;
    step?: number;
    total?: number;
  };
  status?: string;
  thinkingMessages?: IThinkingMessage[];
  thinkingStartTime?: string;
  thinkingEndTime?: string;
}

export interface IChatHistory {
  uuid: string;
  contextUuid: string;
  contextType: ChatContextType;
  name: string;
  created: string;
  /** Present on some unified chat list rows when `messages` are omitted (camelized API field). */
  modified?: string;
  user: IUser;
  messages: IChatMessage[];
  state?: EScheduledPromptState;
  /** Present on unified-list rows for scheduled prompt *report* runs (camelized API field). */
  scheduledPromptUuid?: string | null;
}

export interface IScheduledPromptChat {
  uuid: string;
  name: string;
  created: string;
  user: IUser;
  messages: IChatMessage[];
  contextType: ChatContextType;
  contextUuid: string | null;
  isRead: boolean;
}
